<script setup>
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import FocusSpotlight from 'dashboard/components-next/sidebar/FocusSpotlight.vue';
import {
  useFocusSpotlight,
  findSpotTarget,
} from 'dashboard/composables/useFocusSpotlight';
import { usePageCoach } from 'dashboard/composables/usePageCoach';

const { t } = useI18n();
const {
  currentCoach,
  mode,
  active,
  activeKey,
  activeSelector,
  floating,
  markSeen,
  dismissAll,
  openManual,
} = usePageCoach();
const { spotlightStyle, spotlightRect, setSelector, measure, attach, detach } =
  useFocusSpotlight();

const CARD_WIDTH = 288;
const CARD_HEIGHT = 180;
const ANCHOR_GAP = 12;
const EDGE_GAP = 16;

// Prende um valor entre as duas bordas. Quando a viewport é menor que o
// cartão o mínimo vence, então a dica nunca sai pela esquerda nem pelo topo.
const clampToViewport = (value, limit) =>
  Math.min(Math.max(value, EDGE_GAP), Math.max(EDGE_GAP, limit));

const cardRef = ref(null);
let previousFocus = null;
let onDocKeydown = null;

const isOpen = computed(() => active.value && Boolean(activeKey.value));
const isWhatsNew = computed(() => mode.value === 'whats-new');

const title = computed(() => {
  const coach = currentCoach.value;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return coach ? t(coach.titleKey) : '';
});

const body = computed(() => {
  const coach = currentCoach.value;
  if (!coach) return '';
  if (isWhatsNew.value && coach.whatsNewKey) {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(coach.whatsNewKey);
  }
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(coach.bodyKey);
});

const cardStyle = computed(() => {
  if (floating.value || !spotlightRect.value) {
    return null;
  }
  const box = spotlightRect.value;
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : 1280;
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : 800;
  // Âncora colada na borda esquerda deixava box.left negativo e cortava a
  // dica: agora o clamp vale nos dois lados, na horizontal e na vertical.
  const left = clampToViewport(box.left, viewportWidth - CARD_WIDTH - EDGE_GAP);
  let top = box.bottom + ANCHOR_GAP;
  if (top + CARD_HEIGHT > viewportHeight - EDGE_GAP) {
    // Não cabe embaixo da âncora: vira para cima.
    top = box.top - ANCHOR_GAP - CARD_HEIGHT;
  }
  top = clampToViewport(top, viewportHeight - CARD_HEIGHT - EDGE_GAP);
  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    right: 'auto',
    bottom: 'auto',
  };
});

const restoreFocus = () => {
  if (previousFocus && typeof previousFocus.focus === 'function') {
    previousFocus.focus();
  }
  previousFocus = null;
};

const focusCard = () => {
  nextTick(() => {
    cardRef.value?.querySelector('button')?.focus();
  });
};

const clearSpotlight = () => {
  detach();
  setSelector('');
};

const syncSpotlight = selector => {
  if (!selector) {
    clearSpotlight();
    return;
  }
  attach([selector]);
  setSelector(selector);
  measure();
};

const onOk = () => {
  markSeen(activeKey.value, currentCoach.value?.version);
};

const onDismiss = () => {
  dismissAll();
};

const onOpenManual = () => {
  openManual();
};

onDocKeydown = event => {
  if (!isOpen.value || event.key !== 'Escape') return;
  event.preventDefault();
  markSeen(activeKey.value, currentCoach.value?.version);
};

const attachDoc = () => {
  if (typeof document === 'undefined') return;
  document.addEventListener('keydown', onDocKeydown);
};

const detachDoc = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('keydown', onDocKeydown);
};

watch(
  isOpen,
  open => {
    if (open) {
      previousFocus = previousFocus || document.activeElement;
      attachDoc();
      if (!floating.value && activeSelector.value) {
        syncSpotlight(activeSelector.value);
      } else {
        clearSpotlight();
      }
      focusCard();
      return;
    }
    clearSpotlight();
    detachDoc();
    restoreFocus();
  },
  { immediate: true }
);

watch(activeSelector, selector => {
  if (!isOpen.value) return;
  if (floating.value || !selector) {
    clearSpotlight();
    return;
  }
  if (!findSpotTarget(selector)) {
    clearSpotlight();
    return;
  }
  syncSpotlight(selector);
});

onBeforeUnmount(() => {
  detachDoc();
  clearSpotlight();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <template v-if="isOpen">
      <FocusSpotlight v-if="!floating" :style-value="spotlightStyle" />
      <div
        ref="cardRef"
        role="dialog"
        aria-modal="true"
        :aria-label="t('ONBOARDING.ARIA_LABEL')"
        aria-labelledby="page-coach-title"
        class="z-[80] w-72 max-w-[calc(100vw-2rem)] overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        :class="{ 'fixed bottom-4 end-4': !cardStyle }"
        :style="cardStyle"
        data-testid="page-coach"
        @click.stop
      >
        <div
          v-if="isWhatsNew"
          class="mb-2 inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-semibold text-n-iris-11"
          data-testid="page-coach-whats-new"
        >
          <span class="i-ri-sparkling-2-line size-3.5" aria-hidden="true" />
          {{ t('ONBOARDING.WHATS_NEW') }}
        </div>
        <p
          id="page-coach-title"
          class="m-0 mb-1 text-sm font-semibold text-n-slate-12"
        >
          {{ title }}
        </p>
        <p class="m-0 mb-3 text-sm leading-relaxed text-n-slate-11">
          {{ body }}
        </p>
        <div class="flex flex-col items-stretch gap-2">
          <button
            type="button"
            class="min-h-9 w-full rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold leading-snug text-n-background hover:opacity-90"
            data-testid="page-coach-ok"
            @click="onOk"
          >
            {{ t('ONBOARDING.OK') }}
          </button>
          <button
            v-if="currentCoach?.manualKey"
            type="button"
            class="min-h-9 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="page-manual-open"
            @click="onOpenManual"
          >
            {{ t('ONBOARDING.MANUAL.OPEN') }}
          </button>
          <button
            type="button"
            class="min-h-9 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="page-coach-dismiss"
            @click="onDismiss"
          >
            {{ t('ONBOARDING.DISMISS') }}
          </button>
        </div>
      </div>
    </template>
  </TeleportWithDirection>
</template>
